import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFloppyDisk, FaSpinner, FaCheck } from 'react-icons/fa6';
import { useAllgemeineDaten } from './hooks';
import { AllgemeineDatenProps } from './types';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

/**
 * AllgemeineDaten component for managing Zeitschritte data
 * Refactored to use custom hook for state management
 */
export const AllgemeineDaten: React.FC<AllgemeineDatenProps> = ({
  zeitschritte,
  onChange,
  onDirtyChange,
  sessionId,
  readOnly = false
}) => {
  const { t } = useTranslation('training');
  const { loading, error, isSaving, isDirty, handleChange, clearError, saveData } = useAllgemeineDaten(
    zeitschritte,
    onChange,
    sessionId,
    onDirtyChange
  );

  const inputClasses = `w-full px-3 py-2 bg-card border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${readOnly ? 'bg-muted text-muted-foreground' : ''}`;

  const zeitschrittweiteNum = parseFloat(String(zeitschritte.zeitschrittweite));
  const showOffset = !Number.isFinite(zeitschrittweiteNum) || zeitschrittweiteNum <= 0
    || Math.abs((60 / zeitschrittweiteNum) - Math.round(60 / zeitschrittweiteNum)) < 1e-9;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible mb-4 sm:mb-6">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-center">
          <h2 className="text-base font-semibold text-foreground tracking-tight">
            {t('allgemeineDaten.title')}
          </h2>
          {sessionId && (
            <button
              type="button"
              onClick={saveData}
              disabled={isSaving || loading || readOnly || !isDirty}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${
                isSaving
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : isDirty
                    ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 active:scale-[0.95]'
                    : 'bg-emerald-50 text-emerald-600'
              }`}
              title={isDirty ? t('csvFileUploader.table.saveChanges') : t('csvFileUploader.messages.fileUploaded')}
            >
              {isSaving ? (
                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
              ) : isDirty ? (
                <FaFloppyDisk className="w-3.5 h-3.5" />
              ) : (
                <FaCheck className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200/80 rounded-lg mb-5">
            <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-red-500 text-[10px] font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 leading-relaxed flex-1">{error}</p>
            <button
              type="button"
              onClick={clearError}
              className="text-red-400 hover:text-red-600 text-sm flex-shrink-0"
              aria-label={t('a11y.close')}
            >
              &times;
            </button>
          </div>
        )}

        {/* Form grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
          <div className="flex flex-col">
            <div className="flex items-center gap-1 mb-1.5">
              <label
                htmlFor="allgemeine-daten-eingabe"
                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
              >
                {t('allgemeineDaten.labels.eingabe')}
              </label>
              <InfoTooltip text={t('tooltips.allgemeineDaten.eingabe')} width="w-72" align="start" />
            </div>
            <input
              id="allgemeine-daten-eingabe"
              type="number"
              value={zeitschritte.eingabe || ''}
              onChange={(e) => handleChange('eingabe', e.target.value)}
              onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
              step="1"
              min="1"
              className={`${inputClasses} mt-auto`}
              disabled={readOnly}
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1 mb-1.5">
              <label
                htmlFor="allgemeine-daten-ausgabe"
                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
              >
                {t('allgemeineDaten.labels.ausgabe')}
              </label>
              <InfoTooltip text={t('tooltips.allgemeineDaten.ausgabe')} width="w-72" align="start" />
            </div>
            <input
              id="allgemeine-daten-ausgabe"
              type="number"
              value={zeitschritte.ausgabe || ''}
              onChange={(e) => handleChange('ausgabe', e.target.value)}
              onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
              step="1"
              min="1"
              className={`${inputClasses} mt-auto`}
              disabled={readOnly}
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1 mb-1.5">
              <label
                htmlFor="allgemeine-daten-zeitschrittweite"
                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
              >
                {t('allgemeineDaten.labels.zeitschrittweite')}
              </label>
              <InfoTooltip text={t('tooltips.allgemeineDaten.zeitschrittweite')} width="w-72" align="end" />
            </div>
            <input
              id="allgemeine-daten-zeitschrittweite"
              type="number"
              value={zeitschritte.zeitschrittweite || ''}
              onChange={(e) => handleChange('zeitschrittweite', e.target.value)}
              step="any"
              min="0.0001"
              className={`${inputClasses} mt-auto`}
              disabled={readOnly}
            />
          </div>
          {showOffset && (
          <div className="flex flex-col">
            <div className="flex items-center gap-1 mb-1.5">
              <label
                htmlFor="allgemeine-daten-offset"
                className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
              >
                {t('allgemeineDaten.labels.offset')}
              </label>
              <InfoTooltip text={t('tooltips.allgemeineDaten.offset')} width="w-72" align="end" />
            </div>
            <input
              id="allgemeine-daten-offset"
              type="number"
              value={zeitschritte.offset || ''}
              onChange={(e) => handleChange('offset', e.target.value)}
              step="any"
              min="0"
              className={`${inputClasses} mt-auto`}
              disabled={readOnly}
            />
          </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center mt-5 gap-2">
            <FaSpinner className="animate-spin text-primary text-sm" />
            <span className="text-sm text-muted-foreground">{t('allgemeineDaten.loading')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllgemeineDaten;
